import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line no-restricted-imports
import { Checkbox, Dropdown, DropdownProps, Icon, Input, Label, Popup } from 'semantic-ui-react';
import { didStripeOnboardingSucceed } from '../../../../helpers/stripe';
import DateTime from '../../../DateTime/DateTime';

// Import actions
import { invoicePlanNew } from '../../../../redux/actions/billingActions';
import { invoiceSelected } from '../../../../redux/actions/invoiceActions';

// Import components
import { LoadingIndicator } from '../../../Shared/BusyIndicator';
import NoDataMessageBilling from '../../../Shared/NoDataMessageBilling';
import SectionCard from '../../../Shared/SectionCard';

import { markInvoicesAsArchive, updateOrganizationInvoice } from '../../../../api/firebase/invoices';
import { DefaultDateFormat } from '../../../../helpers/dates';
import { convertListToCSV, currencyFormatter } from '../../../../helpers/utils';
import { InvoiceStatusBadge } from '../index';

// Import styles
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import {
  InvoiceStatus,
  invoiceStatusFormatter,
  isInvoiceElegibleForArchive,
  isV2Invoice,
} from '../../../../helpers/invoices';
import { showSetupPayoutRibbon } from '../../../../helpers/stripe';
import { USER_STATUS } from '../../../../helpers/userStatus';
import { useDelayedPayoutSetup } from '../../../../hooks/useDelayedPayoutSetup';
import { useInvoices } from '../../../../hooks/useInvoices';
import { useOrganization } from '../../../../hooks/useOrganizations';
import { ShowErrors } from '../../../Messages';
import SetupPayoutBanner from '../../../SetupPayoutBanner';
import AlertMessage from '../../../Shared/AppAlert';
import { DateRangePicker } from '../../../Shared/DatePicker';
import './InvoiceList.scss';

import { Invoice } from '@wonderschool/common-base-types';
import {
  ArchiveIcon,
  AutomaticCheckInIcon,
  Button,
  CsvDownloadIcon,
  DataTable,
  EditIcon,
  EyeViewIcon,
  MainContentLayout,
  SlideOver,
  SortByEnum,
  SortDirectionEnum,
  Tooltip,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import compact from 'lodash/compact';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../config/routes';
import { CreateBillingDropdown } from '../../CreateBillingDropdown';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { BillingPageEnum } from '../../../../common';

import { InvoiceDetailContainer } from '../../../Forms/Billing/InvoiceDetail/InvoiceDetailContainer';
import { InvoiceEditDialog, invoiceCanEdit } from '../../../Forms/Billing/InvoiceDetail/InvoiceEditDialog';
import { InvoiceRefundDialog, invoiceCanRefund } from '../../../Forms/Billing/InvoiceDetail/InvoiceRefundDialog';
import { InvoiceStatusDialog, invoiceHasStatusActions } from '../../../Forms/Billing/InvoiceDetail/InvoiceStatusDialog';
import { InvoiceFilters } from '../../../Forms/Billing/InvoicePlans/InvoicePlansList';
import InvoicesAtGlance from '../../FinanceAtGlance/InvoicesAtGlance';
import SetupPayoutRequirements from '../../SetupPayoutRequirements';
import EnablePaymentsModal from '../EnablePaymentsModal';
import { InvoiceActionsMenu } from './InvoiceActionsMenu';

export const INVOICE_TYPE = 'all';

const selectCurrentOrganization = createSelector(
  [(state) => state.organizations],
  (organizations) => organizations?.currentOrganization
);

const PageFilters = ({
  filters,
  setFilters,
}: {
  filters: InvoiceFilters;
  setFilters: (filters: InvoiceFilters) => void;
}) => {
  const { t } = useTranslation();
  const { invoiceStatus, dueDateRange, hideArchived } = filters;

  const statusOptions = useMemo(
    () => [
      { key: InvoiceStatus.DRAFT, value: InvoiceStatus.DRAFT, text: t('common.new') },
      {
        key: USER_STATUS.SCHEDULED,
        value: USER_STATUS.SCHEDULED,
        text: t('status.scheduled'),
      },
      {
        key: InvoiceStatus.OPEN,
        value: InvoiceStatus.OPEN,
        text: t('billing.invoices.status.sent'),
      },
      {
        key: USER_STATUS.NOTIFICATION_SENT,
        value: USER_STATUS.NOTIFICATION_SENT,
        text: t('status.notificationSent'),
      },
      {
        key: USER_STATUS.PAST_DUE,
        value: USER_STATUS.PAST_DUE,
        text: t('status.pastDue'),
      },
      {
        key: USER_STATUS.TRANSFERRING,
        value: USER_STATUS.TRANSFERRING,
        text: t('status.transferring'),
      },
      {
        key: InvoiceStatus.PAID,
        value: InvoiceStatus.PAID,
        text: t('status.deposited'),
      },
      {
        key: `${InvoiceStatus.PAID}(O)`,
        value: `${InvoiceStatus.PAID}(O)`,
        text: t('status.paidManually'),
      },
      {
        key: InvoiceStatus.PROCESSING,
        value: InvoiceStatus.PROCESSING,
        text: t('billing.invoices.status.processing'),
      },
      {
        key: InvoiceStatus.FAILED,
        value: InvoiceStatus.FAILED,
        text: t('billing.invoices.status.failed'),
      },
      {
        key: USER_STATUS.CHARGE_FAILED,
        value: USER_STATUS.CHARGE_FAILED,
        text: t('status.chargeFailed'),
      },
      {
        key: USER_STATUS.TRANSFER_FAILED,
        value: USER_STATUS.TRANSFER_FAILED,
        text: t('status.transferFailed'),
      },
      {
        key: USER_STATUS.REFUNDED,
        value: USER_STATUS.REFUNDED,
        text: t('status.refunded'),
      },
      {
        key: USER_STATUS.REFUND_FAILED,
        value: USER_STATUS.REFUND_FAILED,
        text: t('status.refundFailed'),
      },
      {
        key: USER_STATUS.REFUND_STARTED,
        value: USER_STATUS.REFUND_STARTED,
        text: t('status.refundStarted'),
      },
      {
        key: InvoiceStatus.UNCOLLECTIBLE,
        value: InvoiceStatus.UNCOLLECTIBLE,
        text: t('billing.invoices.status.uncollectible'),
      },
      { key: InvoiceStatus.VOID, value: InvoiceStatus.VOID, text: t('billing.invoices.status.void') },
    ],
    [t]
  );

  const onChange =
    (updateField: string) =>
    (_, { value }) => {
      setFilters({ ...filters, [updateField]: value });
    };

  const onInputChange = (updateField) => (e) => {
    setFilters({ ...filters, [updateField]: e.currentTarget.value });
  };

  const handleChangeStatusFilter = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    setFilters({ ...filters, invoiceStatus: data.value as string });
  };
  return (
    <>
      <div className="invoice-filter-row">
        <Dropdown
          data-testid="invoice-status-filter"
          placeholder={t('billing.invoices.filters.allStatuses')}
          selection
          clearable
          value={invoiceStatus}
          onChange={handleChangeStatusFilter}
          options={statusOptions}
        />
        <Input
          data-testid="invoice-description-filter"
          placeholder={t('billing.invoices.filters.searchDescription')}
          onChange={onInputChange('descriptionSearch')}
        />
        <DateRangePicker
          data-testid="invoice-due-date-filter"
          placeholder={t('billing.invoices.filters.allDueDates')}
          value={dueDateRange}
          clearable
          allowSameEndDate
          onChange={onChange('dueDateRange')}
          className="date-range-picker"
          closeOnMouseLeave={false}
        />
        <Input
          data-testid="invoice-amount-filter"
          placeholder={t('billing.invoices.filters.searchAmount')}
          onChange={onInputChange('amountSearch')}
          className="filter-input"
        />
        <Input
          data-testid="invoice-student-filter"
          placeholder={t('billing.invoices.filters.searchStudentParent')}
          icon="search"
          iconPosition="left"
          onChange={onInputChange('studentSearch')}
        />
      </div>
      <div className="invoice-archive-row">
        <Checkbox
          data-testid="invoice-hide-archived-filter"
          label={t('invoiceList.hideArchiveInvoices')}
          onChange={(e, data) => setFilters({ ...filters, hideArchived: !!data.checked })}
          checked={hideArchived}
        />
      </div>
    </>
  );
};

function InvoicesList({ onClickCreateNewInvoice }: { onClickCreateNewInvoice: () => void }) {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [alertMessage, setAlertMessage] = React.useState<string | null>(null);
  const [isExportLoading, setIsExportLoading] = React.useState(false);
  const csvDownloadColumns = [
    'Id',
    'Students',
    'Parent',
    'Description',
    'Due Date',
    'Invoice Date',
    'Amount',
    'Status',
  ];
  const [filters, setFilters] = React.useState<InvoiceFilters>({
    invoiceStatus: '',
    billingInterval: '',
    descriptionSearch: '',
    startDateRange: '',
    dueDateRange: '',
    amountSearch: '',
    studentSearch: '',
    nextInvoiceDateRange: '',
    hideArchived: true,
    balanceDueSearch: '',
  });
  const [selected, setSelected] = React.useState<any[]>([]);

  const invoices = useInvoices(filters);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClickCreateBillingPlan = useCallback(() => {
    dispatch(invoicePlanNew());
    navigate(routes.billing.plans.edit);
  }, [dispatch, navigate]);

  const { t } = useTranslation();

  const invoiceType = invoices.invoiceType || INVOICE_TYPE;
  const invoicesList = invoices[invoiceType];

  const isEmpty = !!invoices?.isLoaded && invoicesList && !invoicesList.count;

  const accountRequirements = currentOrganization?.stripe?.onboardingRequirements;
  const arePaymentsEnabled = currentOrganization?.stripe?.paymentsEnabled;

  const organization = useOrganization();
  const { paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer, isSelfServeRefundsEnabled } = useFlags();
  // Delayed payout setup: when on, billing is ungated (no paymentsEnabled gate).
  const delayedPayoutEnabled = useDelayedPayoutSetup();
  const isBillingUngated = delayedPayoutEnabled || arePaymentsEnabled;

  const [actionInvoice, setActionInvoice] = React.useState<Invoice | null>(null);
  const [editInvoice, setEditInvoice] = React.useState<Invoice | null>(null);
  const [refundInvoice, setRefundInvoice] = React.useState<Invoice | null>(null);

  // The invoice detail SlideOver and the status dialog both lock page scroll
  // (html overflow:hidden) while open, which resets the list's scroll position
  // to the top on mobile. Capture the scroll position when opening either modal
  // and restore it on close so the list stays where the user was.
  const scrollYBeforeModalRef = React.useRef(0);
  const restoreListScroll = useCallback(() => {
    const y = scrollYBeforeModalRef.current;
    setTimeout(() => window.scrollTo(0, y), 0);
  }, []);
  const openInvoiceDetail = useCallback(
    (row: Invoice) => {
      scrollYBeforeModalRef.current = window.scrollY;
      dispatch(invoiceSelected(row, invoiceType));
    },
    [dispatch, invoiceType]
  );
  const openStatusDialog = useCallback((row: Invoice) => {
    scrollYBeforeModalRef.current = window.scrollY;
    setActionInvoice(row);
  }, []);
  const openEditDialog = useCallback((row: Invoice) => {
    scrollYBeforeModalRef.current = window.scrollY;
    setEditInvoice(row);
  }, []);
  const openRefundDialog = useCallback((row: Invoice) => {
    scrollYBeforeModalRef.current = window.scrollY;
    setRefundInvoice(row);
  }, []);

  const handleInvoiceUpdate = useCallback(
    async (data: Partial<Invoice>) => {
      if (!actionInvoice) return;
      await updateOrganizationInvoice(currentOrganization.id, { ...data, id: data.id || actionInvoice.id });
    },
    [actionInvoice, currentOrganization.id]
  );

  const tableColumns = useMemo(() => {
    function getTotal(invoice: Invoice) {
      if (paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer) {
        return invoice.totalBeforeFees || invoice.total;
      }

      return invoice.total;
    }
    return compact([
      {
        fieldName: 'student.displayName',
        label: t('billing.invoices.columns.students'),
        sortBy: SortByEnum.CLIENT,
        sortFunction: (a, b) => {
          if (!a.student?.displayName || !b.student?.displayName) return 0;

          return a.student.displayName.localeCompare(b.student.displayName);
        },
        renderCell: (row) => {
          return (
            <>
              {isV2Invoice(row) ? (
                // eslint-disable-next-line i18next/no-literal-string
                <Label data-testid={`v2-label-${row.id}`} className="v2-label pointer-events-none" size="mini">
                  v2
                </Label>
              ) : null}
              <div className="pointer-events-none" data-testid={`student-column-value-${row.id}`}>
                {row.student.displayName}
              </div>
              {row.isArchived ? (
                <div
                  data-testid={`invoice-archived-label-${row.id}`}
                  className="invoice-archived-label pointer-events-none"
                >
                  {t('invoiceList.archived.text')}
                </div>
              ) : (
                ''
              )}
            </>
          );
        },
      },
      {
        fieldName: 'description',
        label: t('billing.invoices.columns.description'),
        renderCell: (row) => {
          if (isV2Invoice(row)) {
            return (
              <span className="pointer-events-none" data-testid={`description-column-value-${row.id}`}>
                {row.itemsDescription}
              </span>
            );
          }

          return (
            <span className="pointer-events-none" data-testid={`description-column-value-${row.id}`}>
              {row.invoiceItemList
                .map((c) => {
                  return t(c.item);
                })
                .join(', ')}
            </span>
          );
        },
      },
      {
        fieldName: 'isInvoice',
        label: '',
        renderCell: (row) =>
          !row.isInvoice ? (
            <Popup
              trigger={<Icon data-testid={`recurring-plan-icon-${row.id}`} name="sync" />}
              content={
                <span data-testid={`recurring-plan-tooltip-${row.id}`}>{t('billing.invoices.recurringPlan')}</span>
              }
              offset={[-15, 0]}
            />
          ) : (
            ''
          ),
      },
      {
        fieldName: 'dateDue',
        label: t('billing.invoices.columns.dueDate'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (row) => (
          <span className="pointer-events-none" data-testid={`due-date-column-value-${row.id}`}>
            <DateTime epoch={row.dateDue} format={DefaultDateFormat} />
          </span>
        ),
      },
      {
        fieldName: 'total',
        label: t('billing.invoices.columns.amount'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (row) => (
          <span className="pointer-events-none" data-testid={`amount-column-value-${row.id}`}>
            {currencyFormatter(getTotal(row), { precision: 2 })}
          </span>
        ),
      },
      {
        fieldName: 'status',
        label: t('common.status'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (row) => (
          <span data-testid={`status-column-value-${row.id}`}>
            <InvoiceStatusBadge invoice={row} withTooltip />
          </span>
        ),
      },
      {
        fieldName: '',
        label: '',
        renderCell: (row: Invoice) => {
          // Always show the kebab so the actions are discoverable even on
          // terminal invoices. "Open invoice" is always available (so the menu
          // is never empty); "Change status" only when it's a valid action.
          const actions = [
            {
              label: t('billing.invoices.actions.open'),
              icon: <EyeViewIcon className="size-4" />,
              onClick: () => openInvoiceDetail(row),
            },
          ];
          if (invoiceHasStatusActions(row)) {
            actions.push({
              label: t('billing.invoices.actions.changeStatus'),
              icon: <EditIcon className="size-4" />,
              onClick: () => openStatusDialog(row),
            });
          }
          if (invoiceCanEdit(row)) {
            actions.push({
              label: t('billing.invoice.editInvoice'),
              icon: <EditIcon className="size-4" />,
              onClick: () => openEditDialog(row),
            });
          }
          if (invoiceCanRefund(row, { isSelfServeRefundsEnabled })) {
            actions.push({
              label: t('billing.invoice.startRefundLink'),
              icon: <AutomaticCheckInIcon className="size-4" />,
              onClick: () => openRefundDialog(row),
            });
          }
          return (
            // Stop the click from bubbling to the row (which would open the
            // detail SlideOver) when the user opens the menu. On mobile the row
            // is a stacked card and the kebab is pinned to the card's top-right
            // corner.
            <div
              className="absolute right-2 top-2 sm:static sm:right-auto sm:top-auto"
              onClick={(event) => event.stopPropagation()}
            >
              <InvoiceActionsMenu actions={actions} testId={`invoice-action-btn-${row.id}`} />
            </div>
          );
        },
      },
    ]);
  }, [
    t,
    paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer,
    openInvoiceDetail,
    openStatusDialog,
    openEditDialog,
    openRefundDialog,
    isSelfServeRefundsEnabled,
  ]);

  const canCreateInvoice = useMemo(() => {
    // Delayed payout setup: creating/sending is not gated on payout setup.
    if (delayedPayoutEnabled) return true;
    return arePaymentsEnabled && didStripeOnboardingSucceed(organization);
  }, [delayedPayoutEnabled, arePaymentsEnabled, organization]);

  return (
    <MainContentLayout
      title={t('billing.invoices.title')}
      extraButtons={[
        <Tooltip
          key="create-billing"
          action={canCreateInvoice ? 'none' : 'hover'}
          content={t('invoiceList.connectPayoutAccount')}
        >
          <CreateBillingDropdown
            onCreateInvoice={onClickCreateNewInvoice}
            onCreateBillingPlan={handleClickCreateBillingPlan}
            disabled={!canCreateInvoice}
          />
        </Tooltip>,
      ]}
    >
      <div className="my-4">
        <div className="grid grid-cols-1">
          {!isEmpty && !showSetupPayoutRibbon(organization, delayedPayoutEnabled) && <InvoicesAtGlance />}
          {isBillingUngated ? <PageFilters filters={filters} setFilters={setFilters} /> : null}
        </div>
        <SectionCard header={renderInvoiceListHeader()} className={!isBillingUngated ? 'no-shadow' : ''}>
          {isBillingUngated ? null : <EnablePaymentsModal page={BillingPageEnum.INVOICE} />}
          <SetupPayoutRequirements
            organizationId={currentOrganization?.id}
            accountRequirements={accountRequirements}
            onboardingStatus={currentOrganization?.stripe?.onboardingStatus}
            onError={(error) => setErrorMessage(error)}
            page={BillingPageEnum.INVOICE}
          />
          <SetupPayoutBanner
            onError={(error) => setErrorMessage(error)}
            page={BillingPageEnum.INVOICE}
            accountRequirements={accountRequirements}
          />
          {errorMessage && <ShowErrors content={errorMessage} />}
          {!invoices.isLoaded && <LoadingIndicator />}
          {isEmpty && renderNoDataMessage()}
          {!isEmpty && invoices.isLoaded && renderInvoicesList()}
        </SectionCard>
      </div>
    </MainContentLayout>
  );

  function renderInvoiceListHeader() {
    return (
      <div className="flex justify-end gap-x-4">
        <Button
          data-testid="csv-download-invoices-button"
          disabled={invoicesList.list.length === 0 || isExportLoading}
          size={WidgetSizeEnum.X_SMALL}
          onClick={onClick_CSVDownloadButton}
          preIcon={<CsvDownloadIcon className="size-4" />}
        >
          {isExportLoading && <LoadingIndicator />}
          {t('billing.invoices.actions.download')}
        </Button>
        <Button
          data-testid="archive-invoices-button"
          disabled={selected?.length === 0}
          primary
          size={WidgetSizeEnum.X_SMALL}
          onClick={onClick_ArchiveButton}
          preIcon={<ArchiveIcon className="size-4" />}
        >
          {t('invoiceList.archiveButton.text')}
        </Button>
      </div>
    );
  }

  function renderInvoicesList() {
    return (
      <>
        <AlertMessage alert={alertMessage} setAlert={setAlertMessage} />
        <DataTable
          key={invoicesList.list.length}
          canSelect
          canSelectAll
          hasPagination={true}
          numItemsPerPage={25}
          headerClasses="px-2"
          rowClasses="[&>td:first-child]:w-4 h-16"
          data={Object.values(invoicesList.list)}
          defaultSortConfig={{ name: 'dateDue', direction: SortDirectionEnum.DESC }}
          columns={tableColumns}
          onChangeSelectedRows={(selectedRows) => {
            const selectedCanBeArchived = selectedRows.filter((row) => isInvoiceElegibleForArchive(row));
            setSelected(selectedCanBeArchived);
          }}
          onClickRow={(row: Invoice) => openInvoiceDetail(row)}
          isSelectionDisabled={(row) => !isInvoiceElegibleForArchive(row)}
        />

        {renderInvoiceDetail()}
        {actionInvoice && (
          <InvoiceStatusDialog
            invoice={actionInvoice}
            invoiceType={invoiceType}
            isOpen={!!actionInvoice}
            onClose={() => {
              setActionInvoice(null);
              restoreListScroll();
            }}
            onInvoiceUpdate={handleInvoiceUpdate}
          />
        )}
        {editInvoice && (
          <InvoiceEditDialog
            invoice={editInvoice}
            isOpen={!!editInvoice}
            onClose={() => {
              setEditInvoice(null);
              restoreListScroll();
            }}
            onInvoiceUpdate={(data) =>
              updateOrganizationInvoice(currentOrganization.id, { ...data, id: editInvoice.id })
            }
          />
        )}
        {refundInvoice && (
          <InvoiceRefundDialog
            invoice={refundInvoice}
            organizationId={currentOrganization.id}
            isOpen={!!refundInvoice}
            onClose={() => {
              setRefundInvoice(null);
              restoreListScroll();
            }}
          />
        )}
      </>
    );
  }

  function renderInvoiceDetail() {
    const selectedInvoice = invoicesList.selected;
    const displayName = selectedInvoice?.student?.displayName || '';
    const invoiceId = selectedInvoice?.id || '';

    return (
      <SlideOver
        title={t('billing.invoices.drawer.title', { displayName })}
        subTitle={t('billing.invoices.drawer.subtitle', { invoiceId })}
        isOpen={!!selectedInvoice}
        onClose={() => {
          if (invoiceId) refreshInvoice(currentOrganization, invoiceId);
          dispatch(invoiceSelected(null, invoiceType));
          // Restore the list scroll position the SlideOver's scroll-lock reset.
          restoreListScroll();
        }}
      >
        <InvoiceDetailContainer
          invoices={invoicesList}
          invoiceType={invoiceType}
          initallySelectedInvoice={selectedInvoice}
        />
      </SlideOver>
    );
  }
  function refreshInvoice(_currentOrganization, _invoiceId) {
    //    refreshOrganizationInvoice(currentOrganization.id, invoiceId, INVOICE_TYPE);
  }
  function renderNoDataMessage() {
    return <NoDataMessageBilling title={t('invoiceList.emptyTitle')} subTitle={t('invoiceList.emptySubtitle')} />;
  }

  async function onClick_ArchiveButton() {
    const selectedCanBeArchived = selected.filter((row) => isInvoiceElegibleForArchive(row));
    await markInvoicesAsArchive(currentOrganization.id, selectedCanBeArchived);
    setSelected([]);
  }

  function onClick_CSVDownloadButton() {
    setIsExportLoading(true);
    const formattedList = Object.values(invoicesList.list).map((invoice) => {
      const invoiceItemDescription = isV2Invoice(invoice)
        ? invoice.itemsDescription
        : invoice.invoiceItemList.map((c) => t(c.item)).join(', ');

      const { text: statusText } = invoiceStatusFormatter(invoice, t);

      return {
        invoiceId: invoice.id,
        student: invoice.student.displayName,
        parent: getParentName(invoice),
        description: t(invoiceItemDescription),
        dateDue: moment(invoice.dateDue).format(DefaultDateFormat),
        invoiceDate: moment(invoice.createdAt).format(DefaultDateFormat),
        total: currencyFormatter(invoice.total, { precision: 2 }),
        status: t(statusText),
      };
    });

    //sort formattedList by datedue
    formattedList.sort((x, y) => {
      return moment(x.dateDue).isBefore(y.dateDue) ? -1 : 1;
    });

    try {
      const csvData = convertListToCSV(formattedList, csvDownloadColumns, t);
      const url = window.URL.createObjectURL(new Blob([csvData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${currentOrganization.name}-invoices.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsExportLoading(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExportLoading(false);
    }
  }
}

export function getParentName(invoice) {
  if (isV2Invoice(invoice)) {
    return invoice.billedTo?.displayName || '';
  } else {
    return invoice.student?.responsibleForBilling
      ? invoice.student?.family[invoice.student?.responsibleForBilling.id]?.displayName || ''
      : '';
  }
}

export default InvoicesList;
